import { useState, useEffect } from 'react'
import { Puzzle, Key, Plus, Trash2, Clock, CheckCircle, Copy, AlertTriangle } from 'lucide-react'

interface ExtensionToken {
  token_id: string
  device_name: string
  scopes_json?: string
  scopes?: string[]
  last_used_at: number | null
  expires_at: number | null
  created_at: number
}

function formatDate(ts: number | null): string {
  if (!ts) return '—'
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function TokenRow({ token, onRevoke }: { token: ExtensionToken; onRevoke: (id: string) => void }) {
  const scopes: string[] = token.scopes ?? (token.scopes_json ? JSON.parse(token.scopes_json) : [])
  const isExpired = token.expires_at ? token.expires_at < Date.now() : false

  return (
    <div className={`bg-white border rounded-xl p-4 flex items-center gap-4 ${isExpired ? 'opacity-60 border-red-200' : 'border-gray-200'}`}>
      <div className="w-9 h-9 rounded-lg bg-echo-indigo/10 flex items-center justify-center shrink-0">
        <Puzzle className="w-4 h-4 text-echo-indigo" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-0.5">
          <p className="text-sm font-medium text-gray-900 truncate">{token.device_name}</p>
          {isExpired && (
            <span className="text-xs text-red-600 bg-red-50 px-2 py-0.5 rounded border border-red-200">Expired</span>
          )}
        </div>
        <div className="flex items-center gap-3 text-xs text-gray-400">
          <span className="flex items-center gap-1">
            <Key className="w-3 h-3" />
            {scopes.join(', ') || 'no scopes'}
          </span>
          <span className="flex items-center gap-1">
            <Clock className="w-3 h-3" />
            Last used: {formatDate(token.last_used_at)}
          </span>
          <span>Expires: {formatDate(token.expires_at)}</span>
        </div>
      </div>
      <button
        onClick={() => onRevoke(token.token_id)}
        className="shrink-0 p-2 text-gray-400 hover:text-red-500 rounded-lg hover:bg-red-50 transition-colors"
        title="Revoke token"
      >
        <Trash2 className="w-4 h-4" />
      </button>
    </div>
  )
}

export function ExtensionAuthPage() {
  const [tokens, setTokens] = useState<ExtensionToken[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [deviceName, setDeviceName] = useState('My Browser')
  const [newToken, setNewToken] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [revoking, setRevoking] = useState<string | null>(null)

  useEffect(() => {
    fetchTokens()
  }, [])

  const fetchTokens = async () => {
    setLoading(true)
    try {
      const res = await fetch('/api/v1/extension/tokens')
      if (res.ok) {
        const data = await res.json() as ExtensionToken[]
        setTokens(data)
      }
    } catch {
      // demo mode — show empty
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = async () => {
    if (!deviceName.trim()) return
    setCreating(true)
    setNewToken(null)
    try {
      const res = await fetch('/api/v1/extension/tokens', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ device_name: deviceName, scopes: ['evidence:write'] }),
      })
      if (res.ok) {
        const data = await res.json() as { token: string }
        setNewToken(data.token)
        await fetchTokens()
      }
    } catch {
      // ignore
    } finally {
      setCreating(false)
    }
  }

  const handleRevoke = async (tokenId: string) => {
    setRevoking(tokenId)
    try {
      await fetch(`/api/v1/extension/tokens/${tokenId}`, { method: 'DELETE' })
      setTokens(prev => prev.filter(t => t.token_id !== tokenId))
    } catch {
      // ignore
    } finally {
      setRevoking(null)
    }
  }

  const handleCopy = () => {
    if (!newToken) return
    navigator.clipboard.writeText(newToken).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <div className="max-w-2xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Puzzle className="w-5 h-5 text-echo-indigo" />
          <h1 className="text-xl font-bold text-gray-900">Browser Extension</h1>
        </div>
        <p className="text-sm text-gray-500">
          Generate an auth token for the Echosome browser extension. The extension saves evidence from
          Dovetail, Gong, ChatGPT, and other research tools directly to your workspace.
        </p>
      </div>

      {/* New token banner */}
      {newToken && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-3">
          <div className="flex items-center gap-2 text-sm font-semibold text-emerald-800">
            <CheckCircle className="w-4 h-4" />
            Token created — copy it now, it won't be shown again
          </div>
          <div className="flex items-center gap-2">
            <code className="flex-1 font-mono text-xs bg-white border border-emerald-200 rounded-lg px-3 py-2 text-emerald-900 break-all">
              {newToken}
            </code>
            <button
              onClick={handleCopy}
              className="shrink-0 flex items-center gap-1.5 px-3 py-2 bg-emerald-600 text-white text-xs font-medium rounded-lg hover:bg-emerald-700 transition-colors"
            >
              <Copy className="w-3.5 h-3.5" />
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <div className="flex items-start gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            Paste this token in the Echosome extension popup under Settings → Add Token. It expires in 1 year.
          </div>
        </div>
      )}

      {/* Create new token */}
      <div className="bg-white border border-gray-200 rounded-xl p-5 space-y-3">
        <h2 className="text-sm font-semibold text-gray-800">Create new token</h2>
        <div className="flex items-center gap-3">
          <input
            type="text"
            value={deviceName}
            onChange={e => setDeviceName(e.target.value)}
            placeholder="Device name (e.g. Work MacBook)"
            className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-echo-indigo/30"
          />
          <button
            onClick={handleCreate}
            disabled={creating || !deviceName.trim()}
            className="flex items-center gap-2 px-4 py-2 bg-echo-indigo text-white text-sm font-medium rounded-lg hover:bg-echo-indigo/90 disabled:bg-button-disabled-bg disabled:text-button-disabled-text disabled:cursor-not-allowed transition-colors"
          >
            <Plus className="w-4 h-4" />
            {creating ? 'Creating…' : 'Generate'}
          </button>
        </div>
        <p className="text-xs text-gray-400">
          Scope: <code className="bg-gray-100 px-1 rounded">evidence:write</code> — allows saving research evidence to your workspace.
        </p>
      </div>

      {/* Token list */}
      <section>
        <h2 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">
          Active tokens ({tokens.length})
        </h2>

        {loading ? (
          <div className="space-y-2">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="h-16 bg-gray-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : tokens.length === 0 ? (
          <div className="text-center py-10 text-gray-400">
            <Puzzle className="w-8 h-8 mx-auto mb-2 opacity-30" />
            <p className="text-sm">No tokens yet. Generate one above to connect the extension.</p>
          </div>
        ) : (
          <div className="space-y-2">
            {tokens.map(t => (
              <TokenRow
                key={t.token_id}
                token={t}
                onRevoke={id => {
                  if (!revoking) handleRevoke(id)
                }}
              />
            ))}
          </div>
        )}
      </section>

      {/* Install instructions */}
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-5 space-y-3">
        <h2 className="text-sm font-semibold text-gray-800">Installing the extension</h2>
        <ol className="text-sm text-gray-600 space-y-2 list-decimal list-inside">
          <li>Download the extension from <span className="text-echo-indigo font-medium">chrome://extensions</span> → Load unpacked</li>
          <li>Select the <code className="bg-gray-200 px-1 rounded text-xs">browser-extension/</code> directory</li>
          <li>Click the Echosome icon → Settings → paste your token</li>
          <li>Browse to a Dovetail project, Gong call, or ChatGPT chat and click "Save to Echosome"</li>
        </ol>
      </div>
    </div>
  )
}
